import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import CustomGroupAdminColumnRenderer from './customGroupAdminColumnRenderer.component';

import { CustomGroupPropTypes, CustomGroupsPropTypes } from '../../shared/propTypes/customGroups';
import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import columnDefs from '../../shared/columnDefinitions/customGroups';
import { adminColumnSharedFields } from '../../shared/columnDefinitions';
import { currentSsoSelector } from '../../shared/store/selectors';
import {
  enterEditMode,
  createCustomGroup,
  showDeleteCustomGroupModal,
} from './store/actions';
import { CustomGroupDiscardChangesModalDataPropTypes } from './propTypes';

const newCustomGroup = {
  name: '',
  itemsString: '',
  groupType: '',
  createdBy: '',
  updatedBy: '',
};

class CustomGroupsGrid extends PureComponent {
  static propTypes = {
    customGroups: CustomGroupsPropTypes,
    customGroupsLoading: PropTypes.bool.isRequired,

    selectCustomGroup: PropTypes.func.isRequired,
    createCustomGroup: PropTypes.func.isRequired,
    isCreatingNewCustomGroup: PropTypes.bool.isRequired,
    enterEditMode: PropTypes.func.isRequired,
    showDeleteCustomGroupModal: PropTypes.func.isRequired,
    isCustomGroupModified: PropTypes.bool.isRequired,
    showDiscardChangesConfirmModal: PropTypes.func.isRequired,
    hideDiscardChangesConfirmModal: PropTypes.func.isRequired,
    selectedCustomGroup: CustomGroupPropTypes,
    sso: PropTypes.string.isRequired,
    discardChangesConfirmDialogData: CustomGroupDiscardChangesModalDataPropTypes,
  };

  componentDidUpdate(prevProps) {
    const { isCreatingNewCustomGroup } = this.props;
    const { isCreatingNewCustomGroup: prevIsCreatingNewCustomGroup } = prevProps;

    if (isCreatingNewCustomGroup && !prevIsCreatingNewCustomGroup) {
      this.selectFirstRow();
    }
  }

  onGridReady = params => {
    this.api = params.api;
  };

  adminColumn = (addingNewGroup = false) => {
    const { sso, enterEditMode, showDeleteCustomGroupModal } = this.props;
    return {
      valueGetter: ({ data: { id, allowEditFlag, publicFlag, ownerSso, name } }) =>
        ({ id, name, allowEditFlag, publicFlag, ownerSso }),
      cellRenderer: CustomGroupAdminColumnRenderer,
      cellRendererParams: {
        sso,
        addingNewGroup,
        placeholderForNewGroup: '<new custom group>',
        enterEditMode,
        showDeleteCustomGroupModal,
      },
      colSpan: params => {
        const { addingNewGroup } = params.column.colDef.cellRendererParams;
        return addingNewGroup && !params.data.name.length ? 6 : 1;
      },
      ...adminColumnSharedFields,
    };
  };

  columns = [this.adminColumn(true)].concat(columnDefs);

  selectFirstRow = () => {
    const firstRow = this.api.getDisplayedRowAtIndex(0);

    if (firstRow) {
      const deselectOthers = true;
      firstRow.setSelected(true, deselectOthers);
    }
  };

  selectRowByGroupId = customGroupId => {
    setTimeout(() => {
      this.api.forEachNode(node => {
        if (node.data.id === customGroupId && !node.data.selected) {
          node.setSelected(true);
        }
      });
    });
  };

  onDiscardChangesConfirm = () => {
    const {
      selectCustomGroup,
      hideDiscardChangesConfirmModal,
      discardChangesConfirmDialogData: { data: { selectedCustomGroupId } },
    } = this.props;
    selectCustomGroup(selectedCustomGroupId);
    hideDiscardChangesConfirmModal();
    this.selectRowByGroupId(selectedCustomGroupId);
  };

  selectCustomGroup = params => {
    const {
      selectedCustomGroup,
      isCustomGroupModified,
      selectCustomGroup,
      showDiscardChangesConfirmModal,
      isCreatingNewCustomGroup,
    } = this.props;
    const { api, node } = params;

    if (node.selected) {
      const selectedRows = api.getSelectedRows();

      if (selectedRows.length <= 0 || !node.data.id) {
        return;
      }

      const customGroup = selectedRows[0];

      if (isCustomGroupModified) {
        if (isCreatingNewCustomGroup) {
          this.selectFirstRow();
        } else {
          this.selectRowByGroupId(selectedCustomGroup.id);
        }
        showDiscardChangesConfirmModal({
          onConfirm: this.onDiscardChangesConfirm,
          selectedCustomGroupId: node.data.id,
        });
        return;
      }

      selectCustomGroup(customGroup.id);
    }
  };

  createCustomGroup = () => {
    const { createCustomGroup, enterEditMode } = this.props;
    createCustomGroup();
    enterEditMode();
  };

  render() {
    const {
      customGroups,
      customGroupsLoading,
      isCreatingNewCustomGroup,
    } = this.props;

    const rowData = isCreatingNewCustomGroup ? [newCustomGroup].concat(customGroups) : customGroups;

    return (
      <div className="ag-grid-full-page-with-secondary-menu">
        <AgGrid
          componentToRenderOnSearchBar={
            <Button
              type="button"
              kind="primary"
              className="a-my-10"
              onClick={this.createCustomGroup}
            >
              Add Custom Group
            </Button>
          }
          columnDefs={this.columns}
          rowData={rowData}
          onRowDataUpdated={this.rowDataChanged}
          withSearchBar
          isGridLoading={customGroupsLoading}
          onGridReady={this.onGridReady}
          onCellClicked={this.selectCustomGroup}
          autoSelectFirstRowOnFirstLoad
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  sso: currentSsoSelector(state),
});

export default connect(mapStateToProps, {
  enterEditMode,
  createCustomGroup,
  showDeleteCustomGroupModal,
})(CustomGroupsGrid);
